import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { motion, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";

// ─── Easing for count-up ─────────────────────────────────────────────
function easeOutExpo(t) {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function useCountUp(target, duration = 2200, decimals = 0, started = false) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!started) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutExpo(progress);
      setValue(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
      else setValue(target);
    };
    requestAnimationFrame(step);
  }, [started, target, duration, decimals]);

  return value;
}

// ─── Styled ──────────────────────────────────────────────────────────
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const Section = styled.section`
  position: relative;
  width: 100%;
  background: var(--ink);
  overflow: hidden;
  padding: 9rem 7vw;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
    opacity: 0.5;
  }
`;

const BgNumeral = styled(motion.span)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: var(--font-serif);
  font-size: clamp(18rem, 30vw, 36rem);
  font-weight: 300;
  font-style: italic;
  color: transparent;
  -webkit-text-stroke: 1px rgba(201, 170, 140, 0.06);
  line-height: 1;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
  z-index: 0;
`;

const TopRule = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 7vw;
  right: 7vw;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--gold) 20%,
    var(--gold) 80%,
    transparent 100%
  );
  opacity: 0.3;
  transform-origin: left;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
const EyebrowDash = styled(motion.span)`
  display: block;
  width: 36px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.8;
`;
const Eyebrow = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.63rem;
  font-weight: 300;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--terracotta);
`;

const HeadingWrap = styled.div`
  overflow: hidden;
`;
const Heading = styled(motion.h2)`
  font-family: var(--font-serif);
  font-size: clamp(2.6rem, 4.5vw, 4.2rem);
  font-weight: 300;
  color: var(--cream);
  letter-spacing: -0.01em;
`;
const HeadingItalic = styled(Heading)`
  font-style: italic;
  color: var(--warm-tan);
`;

const HeaderRight = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.8rem;
  font-weight: 200;
  letter-spacing: 0.06em;
  line-height: 1.8;
  color: rgba(247, 243, 238, 0.45);
  max-width: 32ch;
  text-align: right;

  @media (max-width: 700px) {
    text-align: left;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(motion.div)`
  position: relative;
  padding: 3rem 2.5rem 3.5rem;
  border-left: 1px solid rgba(201, 170, 140, 0.1);
  cursor: default;

  &:first-child {
    border-left: none;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 80% 60% at 30% 80%,
      rgba(184, 115, 85, 0.07) 0%,
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.5s ease;
    pointer-events: none;
  }
  &:hover::before {
    opacity: 1;
  }

  @media (max-width: 900px) {
    &:nth-child(3) {
      border-left: none;
    }
    border-top: 1px solid rgba(201, 170, 140, 0.1);
    &:nth-child(1),
    &:nth-child(2) {
      border-top: none;
    }
  }
`;

const NumberRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.1em;
  margin-bottom: 1.2rem;
  line-height: 1;
  padding-bottom: 0.15em;
`;

const BigNumber = styled.span`
  font-family: var(--font-serif);
  font-size: clamp(4rem, 6vw, 6.5rem);
  font-weight: 300;
  color: var(--cream);
  line-height: 1;
  letter-spacing: -0.03em;
  padding-bottom: 0.1em;
  display: inline-block;
  background: linear-gradient(
    90deg,
    var(--cream) 0%,
    var(--warm-tan) 40%,
    var(--cream) 60%,
    var(--cream) 100%
  );
  background-size: 800px 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${({ $counting }) => ($counting ? shimmer : "none")} 1.6s linear
    infinite;
`;

const Suffix = styled.span`
  font-family: var(--font-serif);
  font-size: clamp(2rem, 3vw, 3.2rem);
  font-weight: 300;
  font-style: italic;
  color: var(--terracotta);
  line-height: 1;
  padding-bottom: 0.15em;
`;

const StatLabel = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--cream);
  opacity: 0.9;
  margin-bottom: 0.5rem;
`;

const StatSub = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: 0.68rem;
  font-weight: 200;
  letter-spacing: 0.06em;
  line-height: 1.6;
  color: rgba(247, 243, 238, 0.38);
`;

const BottomRule = styled(motion.div)`
  height: 1px;
  background: linear-gradient(
    90deg,
    var(--terracotta) 0%,
    var(--gold) 50%,
    transparent 100%
  );
  opacity: 0.25;
  transform-origin: left;
`;

// ─── Animation variants ───────────────────────────────────────────────
const revealUp = {
  hidden: { y: "105%" },
  show: { y: "0%", transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } },
};

const fadeSlideUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
  },
});

// ─── Single stat item ─────────────────────────────────────────────────
function StatItem({ stat, index, started }) {
  const count = useCountUp(stat.number, stat.duration, stat.decimals, started);
  const [wasCounting, setWasCounting] = useState(false);

  useEffect(() => {
    if (started) setWasCounting(true);
    const t = setTimeout(() => setWasCounting(false), stat.duration + 200);
    return () => clearInterval(t);
  }, [started, stat.duration]);

  return (
    <StatCard
      variants={fadeSlideUp(index * 0.12)}
      initial="hidden"
      animate={started ? "show" : "hidden"}
      whileHover={{ y: -4, transition: { duration: 0.3 } }}
    >
      <NumberRow>
        <BigNumber $counting={wasCounting && started}>{count}</BigNumber>
        <Suffix>{stat.suffix}</Suffix>
      </NumberRow>

      <StatLabel
        variants={fadeSlideUp(index * 0.12 + 0.2)}
        initial="hidden"
        animate={started ? "show" : "hidden"}
      >
        {stat.label}
      </StatLabel>

      <StatSub
        variants={fadeSlideUp(index * 0.12 + 0.3)}
        initial="hidden"
        animate={started ? "show" : "hidden"}
      >
        {stat.sub}
      </StatSub>
    </StatCard>
  );
}

// ─── Main Component ───────────────────────────────────────────────────
export default function StatsSection() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-15%" });

  const STATS = [
    {
      id: 0,
      number: 440,
      suffix: "+",
      decimals: 0,
      label: t("stats.items.clients.label"),
      sub: t("stats.items.clients.sub"),
      duration: 2000,
    },
    {
      id: 1,
      number: 8,
      suffix: "+",
      decimals: 0,
      label: t("stats.items.years.label"),
      sub: t("stats.items.years.sub"),
      duration: 1400,
    },
    {
      id: 2,
      number: 98,
      suffix: "%",
      decimals: 0,
      label: t("stats.items.rebooking.label"),
      sub: t("stats.items.rebooking.sub"),
      duration: 1800,
    },
    {
      id: 3,
      number: 500,
      suffix: "+",
      decimals: 0,
      label: t("stats.items.looks.label"),
      sub: t("stats.items.looks.sub"),
      duration: 2400,
    },
  ];

  return (
    <Section ref={ref}>
      <BgNumeral
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 1.2, delay: 0.3 }}
      >
        8
      </BgNumeral>

      <TopRule
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      />

      <Inner>
        <Header>
          <div>
            <EyebrowRow style={{ marginBottom: "1.2rem" }}>
              <EyebrowDash
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                style={{ transformOrigin: "left" }}
              />
              <Eyebrow
                initial={{ opacity: 0, x: -10 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.35, duration: 0.6 }}
              >
                {t("stats.eyebrow")}
              </Eyebrow>
            </EyebrowRow>

            <div>
              <HeadingWrap>
                <Heading
                  variants={revealUp}
                  initial="hidden"
                  animate={isInView ? "show" : "hidden"}
                  transition={{ delay: 0.3 }}
                >
                  {t("stats.heading_1")}
                </Heading>
              </HeadingWrap>
              <HeadingWrap>
                <HeadingItalic
                  as={motion.h2}
                  variants={revealUp}
                  initial="hidden"
                  animate={isInView ? "show" : "hidden"}
                  transition={{ delay: 0.42 }}
                >
                  {t("stats.heading_2")}
                </HeadingItalic>
              </HeadingWrap>
            </div>
          </div>

          <HeaderRight
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            {t("stats.description")}
          </HeaderRight>
        </Header>

        <Grid>
          {STATS.map((stat, i) => (
            <StatItem key={stat.id} stat={stat} index={i} started={isInView} />
          ))}
        </Grid>

        <BottomRule
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ delay: 0.8, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </Inner>
    </Section>
  );
}
